import {Flex, Text} from '@chakra-ui/react';
import React from 'react';
import {useTranslation} from 'react-i18next';
import {FilterBarBlueCheckbox} from '@/components/FilterBar/FilterBarBlueCheckbox';

export interface FromageFacets {
  famille_fromage: [string, number][];
  type_de_lait: [string, number][];
  traitement_thermique_du_lait: [string, number][];
}

interface FromageFilterContentProps {
  facets: FromageFacets;
  selectedFamilleFromage: string[];
  selectedTypeDeLait: string[];
  selectedTraitementDuLait: string[];
  onFamilleFromageChange: (values: string[]) => void;
  onTypeDeLaitChange: (values: string[]) => void;
  onTraitementDuLaitChange: (values: string[]) => void;
}

interface FromageSectionProps {
  titleKey: string;
  labelPrefix: string;
  facetEntries: [string, number][];
  selected: string[];
  onChange: (values: string[]) => void;
}

// Section d'un filtre fromage (Famille, Type de lait, Traitement thermique).
// Affiche un titre puis une checkbox bleue par facette non vide, et gère le toggle
// d'une valeur dans la sélection courante. Masqué si aucune facette.
const FromageSection: React.FC<FromageSectionProps> = ({
  titleKey,
  labelPrefix,
  facetEntries,
  selected,
  onChange,
}) => {
  const {t} = useTranslation();
  if (facetEntries.length === 0) return null;

  const toggle = (code: string, checked: boolean) => {
    onChange(
      checked ? [...selected, code] : selected.filter((c) => c !== code),
    );
  };

  return (
    <Flex
      direction='column'
      gap={1}
    >
      <Text
        fontSize='xs'
        fontWeight='700'
        color='gray.500'
        textTransform='uppercase'
      >
        {t(titleKey)}
      </Text>
      {facetEntries.map(([code, count]) =>
        count ? (
          <FilterBarBlueCheckbox
            key={code}
            isChecked={selected.includes(code)}
            onChange={(checked) => {
              toggle(code, checked);
            }}
          >
            {t(`${labelPrefix}.${code}`)} ({count})
          </FilterBarBlueCheckbox>
        ) : null,
      )}
    </Flex>
  );
};

// Composant partagé entre catalogue et cadencier pour le filtre "Caractéristiques fromage"
export const FromageFilterContent: React.FC<FromageFilterContentProps> = ({
  facets,
  selectedFamilleFromage,
  selectedTypeDeLait,
  selectedTraitementDuLait,
  onFamilleFromageChange,
  onTypeDeLaitChange,
  onTraitementDuLaitChange,
}) => (
  // 3 colonnes desktop (Type de lait → Type de fromage → Traitement), une colonne mobile.
  // Doit rester en sync avec `contentMaxW='700px'` posé sur le `FilterBarPopoverFilter`
  <Flex
    direction={{base: 'column', md: 'row'}}
    gap={{base: 3, md: 4}}
    align='flex-start'
    p={1}
  >
    {/* Type de fromage */}
    <FromageSection
      titleKey='catalogue.filtre.famille_fromage'
      labelPrefix='fromage.famille'
      facetEntries={facets.famille_fromage}
      selected={selectedFamilleFromage}
      onChange={onFamilleFromageChange}
    />
    {/* Type de lait */}
    <FromageSection
      titleKey='catalogue.filtre.type_de_lait'
      labelPrefix='fromage.lait'
      facetEntries={facets.type_de_lait}
      selected={selectedTypeDeLait}
      onChange={onTypeDeLaitChange}
    />
    {/* Traitement du lait */}
    <FromageSection
      titleKey='catalogue.filtre.traitement_thermique_du_lait'
      labelPrefix='fromage.traitement'
      facetEntries={facets.traitement_thermique_du_lait}
      selected={selectedTraitementDuLait}
      onChange={onTraitementDuLaitChange}
    />
  </Flex>
);
